import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { Box, DropdownMenu, Icon, Separator } from '@betterment/samba-react';

import { Section } from './MenuContents/DropdownMenuContent';

import { trackClick } from '../../../../../features/track';
import { accountsByType } from '../../../../shared/utils';
import { PerformanceAccountsQuery } from '../../../graphql/performanceAccounts.generated';

export type AccountsFilterProps = {
  accounts: PerformanceAccountsQuery['performance']['accounts'];
  selectedAccountIds: Set<string>;
  selectedSetter: Dispatch<SetStateAction<Set<string>>>;
};

export const AccountsFilter: React.FC<AccountsFilterProps> = ({
  accounts,
  selectedSetter,
  selectedAccountIds = new Set(),
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { cashAccounts, investingAccounts } = accountsByType(accounts);

  const toggleAccountSelection = useCallback(
    (accountId: string) => {
      const updatedIds = new Set(selectedAccountIds);
      if (updatedIds.has(accountId)) {
        updatedIds.delete(accountId);
      } else {
        updatedIds.add(accountId);
      }
      selectedSetter(updatedIds);

      trackClick({
        name: 'accountFilterInteraction',
        module: 'Performance',
        location: 'Balance',
      });
    },
    [selectedAccountIds, selectedSetter]
  );

  // Don't show the filter if there is less than two accounts
  if (accounts.length <= 1) {
    return null;
  }

  const filterLabel =
    selectedAccountIds.size > 0
      ? `${selectedAccountIds.size} account${selectedAccountIds.size > 1 ? 's' : ''}`
      : 'Filter accounts';

  return (
    <DropdownMenu onOpenChange={setIsExpanded} modal={false}>
      <DropdownMenu.Trigger asChild={true}>
        <Box css={{ display: 'flex', alignItems: 'center', pointerEvents: 'auto', cursor: 'pointer' }}>
          {filterLabel}
          <Icon
            name="CaretDown"
            size="small"
            color="icon"
            css={{
              transition: 'transform 0.1s ease-in-out',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align="end"
        css={{
          display: 'grid',
          px: 'space.300',
          py: 'space.300',
          maxHeight: '45vh',
          overflowY: 'scroll',
          gap: 'space.200',
        }}
      >
        <Section
          showSeparator={false}
          accounts={investingAccounts}
          selectedAccountIds={selectedAccountIds}
          toggleAccountSelection={toggleAccountSelection}
          header="Investing"
        />
        {cashAccounts.length > 0 && investingAccounts.length > 0 && <Separator />}
        <Section
          accounts={cashAccounts}
          selectedAccountIds={selectedAccountIds}
          toggleAccountSelection={toggleAccountSelection}
          header="Cash"
        />
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
